import { DestroyRef, EventEmitter, inject, Injectable } from '@angular/core';
import { Observable, firstValueFrom, map, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ProjectXEpaXModelInputsDTO } from 'src/app/shared/models/project-x-epa-x-model-inputs-dto';
import { ProjectXEpaXModelInputsRequest } from 'src/app/shared/models/project-x-epa-x-model-inputs-request';
import { DetectUnsavedChanges } from 'src/app/misc/detect-unsaved-changes';
import { UdlModelService } from '../../udl-model.service';
import { Project } from 'src/app/shared/models/project';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ESATabService } from '../../udl-overlap-tool/esa-tab.service';
import { UdlOverlapToolContainerService } from '../../udl-overlap-tool-container.service';
import { EPAProjectRunService } from '../../esa-output/project-status/esa-project-run.api.service';
import { Constants } from 'src/app/shared/utils/constants';
import Swal from 'sweetalert2';

@Injectable({
    providedIn: 'root'
})
export class EPAProjectInputsService {
    private tabService: ESATabService = inject(ESATabService);
    private selectedProjectApiService: SelectedProjectApiService = inject(SelectedProjectApiService);
    private udlOverlapToolContainerService: UdlOverlapToolContainerService = inject(UdlOverlapToolContainerService);
    private projectRunService: EPAProjectRunService = inject(EPAProjectRunService);

    private modelService: UdlModelService = inject(UdlModelService);
    private httpClient: HttpClient = inject(HttpClient);
    private destroyRef = inject(DestroyRef)
    private apiUrl: string = environment.BAMS_Api_URL;
    private inputsWithSavedChangeDetection = new DetectUnsavedChanges<ProjectXEpaXModelInputsDTO>();
    private hasEmittedSaveProjectEvent: boolean = false;
    private changesDetectedInNameOrDescription: boolean = false;
    selectedProject?: Project;
    validSetup: boolean = false;

    public getAndSetInputsEvent: EventEmitter<number> = new EventEmitter<number>();
    public changedProjectEvent: EventEmitter<void> = new EventEmitter<void>();

    constructor() {
        this.selectedProjectApiService.selectedProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(project => {
            this.selectedProject = project;
        });
    }

    public setSelectedEsaInputs(inputsDto: ProjectXEpaXModelInputsDTO) {
        this.inputsWithSavedChangeDetection.setObject(inputsDto);
    }

    public saveProjectEpaInputs(projectPk: number): Observable<boolean> {
        const request: ProjectXEpaXModelInputsRequest = {
            data: [this.inputsWithSavedChangeDetection.getCurrentValue()!]
        }
        request.data.forEach(inputs => { inputs.project_pk = projectPk });
        const endpoint = `${this.apiUrl}api/projects/epa/InputsModel/`;
        const body = JSON.stringify(request);
        return this.httpClient.put(endpoint, body).pipe(map((data: any) => {
            this.resetChangeDetection();
            return data.Value
        }));
    }

    public getProjectEpaInputs(projectPk: number): Observable<any> {
        const endpoint = `${this.apiUrl}api/projects/${projectPk}/epa/InputsModel/`;
        return this.httpClient.get(endpoint).pipe(map((data: any) => data.Value));
    }

    public getUnsavedChangesExist(): boolean {
        return this.inputsWithSavedChangeDetection.hasUnsavedChanges() || this.changesDetectedInNameOrDescription;
    }

    public resetChangeDetection(): void {
        this.inputsWithSavedChangeDetection.markAsSaved();
        this.changesDetectedInNameOrDescription = false;
        this.hasEmittedSaveProjectEvent = false;
    }

    public setProjectNameAndDescriptionChanged() {
        this.changesDetectedInNameOrDescription = true;
    }

    public getCropUseValuesForRegions() {
        const endpoint = `${this.apiUrl}api/values/model/v${this.modelService.modelVersion}/GetCropUseValuesForRegions`;
        return this.httpClient.get(endpoint).pipe(map((data: any) => data.Value));
    }

    public async saveProjectIfUnsavedChangesExist(isProjectToBeImported: boolean = false) {
        if (!this.hasEmittedSaveProjectEvent && (this.getUnsavedChangesExist() || isProjectToBeImported)) {
            this.hasEmittedSaveProjectEvent = true;
            await this.SaveSelectedProject();
        }
    }


    async SaveSelectedProject() {
        let validatedName = await this.validateName();
        if (!this.selectedProject || !validatedName) {
            this.tabService.setSuccess(true);
            return;
        }

        this.selectedProjectApiService.saveProject(Constants.FEATURE_ACRONYM.ESA_ASSESSMENT_UDL).subscribe(projectPk => {
            this.saveProjectEpaInputs(projectPk).subscribe(() => {
                this.udlOverlapToolContainerService.updateProjectFiles(projectPk)
                this.projectRunService.resetRunStatus();
            });
        });

        this.udlOverlapToolContainerService.projectFilesUpdatedEvent.subscribe(projectPk => {
            this.selectedProjectApiService.addProjectToDropdown(projectPk);
            this.tabService.setSuccess(true);
        });
    }

    async validateName(): Promise<boolean> {
        if (!this.selectedProject?.name) return false;
        return await firstValueFrom(this.selectedProjectApiService.projects).then(projects => {
            let isValid = true;
            if (projects.find(p => p.name === this.selectedProject?.name && p.projectPk !== this.selectedProject?.projectPk)) {
                isValid = false;
                this.tabService.setSuccess(true);
                Swal.fire({
                    title: `${this.selectedProject?.name}, project name already exists.`,
                    text: 'Please use another name',
                    confirmButtonColor: '#0069be',
                    confirmButtonText: 'Ok',
                    icon: 'error',
                });
            }
            this.validSetup = isValid;
            return isValid;
        });
    }

}
